/**
 * Pure helpers for the booking-import flow (Tripsby Together — P4).
 *
 * The BookingImportDialog parses a forwarded hotel confirmation via the
 * concierge-builder API, lets the host review/edit the fields, then commits the
 * reservation through the existing host-assign path (groupsApi.assignReservation
 * -> POST /api/groups/{id}/room-requests). These helpers isolate the
 * guest-matching, room-block-matching, validation, and request-body assembly so
 * they can be unit-tested without rendering the dialog.
 */
import type {
  AssignReservationRequest,
  GroupInvitee,
  GroupRoomBlock,
} from '../types/groups';

export function normalizeEmail(email?: string | null): string {
  return (email || '').trim().toLowerCase();
}

/**
 * Match the extracted guest email against the group's invitees (case-insensitive,
 * trimmed). Returns the first matching invitee, or undefined if none match.
 */
export function matchInviteeByEmail(
  email: string | null | undefined,
  invitees: GroupInvitee[],
): GroupInvitee | undefined {
  const target = normalizeEmail(email);
  if (!target) return undefined;
  return invitees.find((i) => normalizeEmail(i.email) === target);
}

/**
 * Suggest a room block for the extracted hotel name via case-insensitive
 * substring matching in either direction (e.g. "Marriott" ~ "Chicago Marriott
 * Downtown"). Returns undefined when nothing plausibly matches so the host must
 * pick explicitly.
 */
export function suggestRoomBlock(
  hotelName: string | null | undefined,
  blocks: GroupRoomBlock[],
): GroupRoomBlock | undefined {
  const hotel = (hotelName || '').trim().toLowerCase();
  if (!hotel) return undefined;
  return blocks.find((b) => {
    const name = (b.hotelName || '').trim().toLowerCase();
    if (!name) return false;
    return name.includes(hotel) || hotel.includes(name);
  });
}

export interface BookingAssignInput {
  roomBlockId: string;
  /** Set when assigning to an existing invitee; the backend hydrates contact info. */
  inviteeId?: string | null;
  /** Used only when inviteeId is not set (extracted guest not in the invitee list). */
  guestName?: string | null;
  guestEmail?: string | null;
  guestPhone?: string | null;
  confirmationNumber?: string | null;
  arrivalDate?: string | null;
  departureDate?: string | null;
  rateAmount?: string | number | null;
  rateCurrency?: string | null;
  /** The room named on the forwarded confirmation. Blank keeps the block's. */
  roomType?: string | null;
}

function toNumberOrUndefined(value: string | number | null | undefined): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Blocking validation errors for a booking assignment. Empty array = OK to submit.
 * Guards the two mis-parse failure modes: no target guest, and check-out before
 * check-in.
 */
export function validateBookingAssign(input: BookingAssignInput): string[] {
  const errors: string[] = [];
  if (!input.roomBlockId) {
    errors.push('Pick a room block for this reservation.');
  }
  const hasInvitee = !!(input.inviteeId && input.inviteeId.trim());
  const hasGuestName = !!(input.guestName && input.guestName.trim());
  if (!hasInvitee && !hasGuestName) {
    errors.push('Pick a guest, or provide the guest name from the confirmation.');
  }
  if (input.arrivalDate && input.departureDate && input.arrivalDate >= input.departureDate) {
    errors.push('Check-out must be after check-in.');
  }
  return errors;
}

/**
 * Assemble the AssignReservationRequest body for a confirmed booking import.
 * Always status "booked" (which fires the existing stay-confirmation email).
 *
 * - When inviteeId is set, the guest is an existing invitee and contact fields
 *   are left off so the backend hydrates them from group_invitees.
 * - Otherwise the extracted guest name/email/phone are sent directly.
 */
export function buildAssignReservationBody(input: BookingAssignInput): AssignReservationRequest {
  const body: AssignReservationRequest = {
    roomBlockId: input.roomBlockId,
    status: 'booked',
    quantity: 1,
    partySize: 1,
  };

  const hasInvitee = !!(input.inviteeId && input.inviteeId.trim());
  if (hasInvitee) {
    body.inviteeId = input.inviteeId!.trim();
  } else {
    body.guestName = (input.guestName || '').trim() || undefined;
    body.email = (input.guestEmail || '').trim() || undefined;
    body.phone = (input.guestPhone || '').trim() || undefined;
  }

  const confirmation = (input.confirmationNumber || '').trim();
  if (confirmation) body.bookedConfirmation = confirmation;

  if (input.arrivalDate) body.arrivalDate = input.arrivalDate;
  if (input.departureDate) body.departureDate = input.departureDate;

  const rate = toNumberOrUndefined(input.rateAmount);
  if (rate !== undefined) body.rateAmount = rate;

  const currency = (input.rateCurrency || '').trim();
  if (currency) body.rateCurrency = currency.toUpperCase();

  // The room type read off the forwarded confirmation. It was parsed, shown to
  // the host in an editable field, and then dropped here — so a reservation
  // imported from a real hotel email still described the block's room.
  const roomType = (input.roomType || '').trim();
  if (roomType) body.roomType = roomType;

  return body;
}
